import { Navigate, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Spinner, ErrorState } from '@/components/states'
import { useAuth } from '@/features/auth/AuthContext'

function FullPage({ children }: { children: ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center p-6">{children}</div>
}

/**
 * FR-1.1 — every route except /signin, /403 and /404 requires an authenticated,
 * permitted account. FR-1.4 — the requested route is carried through sign-in in
 * the `from` state so deep links survive authentication.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth()
  const location = useLocation()
  const { t } = useTranslation()

  if (status === 'initialising' || status === 'signing-in') {
    return (
      <FullPage>
        <Spinner label={t('state.loading')} />
      </FullPage>
    )
  }

  if (status === 'signed-out') {
    return <Navigate to="/signin" replace state={{ from: location }} />
  }

  if (status === 'not-permitted') return <Navigate to="/403" replace />

  if (status === 'error') {
    return (
      <FullPage>
        <ErrorState error={null} onRetry={() => window.location.reload()} />
      </FullPage>
    )
  }

  return <>{children}</>
}

/**
 * Hiding the admin UI is cosmetic — the API re-checks the role on every
 * mutating endpoint (§4.3) — but a non-admin should still never land here.
 */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const { hasRole } = useAuth()
  if (!hasRole('admin')) return <Navigate to="/403" replace state={{ reason: 'forbidden' }} />
  return <>{children}</>
}
